import { DailyVideo, useDaily, useLocalSessionId } from "@daily-co/daily-react";
import { Mic, MicOff, Video, VideoOff } from "@tamagui/lucide-icons";
import { useToastController } from "@tamagui/toast";
import { LinearGradient } from "expo-linear-gradient";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Theme, getTokenValue } from "tamagui";

import {
  useGetClinicStatusForPatient,
  useGetClinicianWaitTime,
} from "@/api/generated";
import { ClientEventModel } from "@/api/model/Conversation";
import { useGetUserProfile } from "@/api/queries/userProfile";
import {
  Button,
  CloseButton,
  Footer,
  Header,
  Heading,
  Image,
  Link,
  LinkText,
  Paragraph,
  ScrollView,
  Text,
  XStack,
  YStack,
} from "@/components";
import Ellipses from "@/components/animated/Ellipses";
import Logo from "@/components/Logo";
import { medicalGroupName } from "@/config/brand";
import { useInfiniteMessages } from "@/hooks/useInfiniteMessages";
import { usePropagatedRouter } from "@/hooks/usePropagatedRouter";
import { useVideoPreview } from "@/hooks/video";
import { captureException } from "@/sentry";
import { VideoModes, useVideoStore } from "@/store";
import { logMediaPermissions } from "@/utils/permissions";
import DeviceSelector from "./DeviceSelector";
import PermissionsDialog from "./PermissionsDialog";

const DEFAULT_WAIT_TIME_COPY = "3-5 min average wait time";

interface VideoPreviewProps {
  onClose: () => void;
  curaiConversationId: string;
}

const VideoPreview = ({ onClose, curaiConversationId }: VideoPreviewProps) => {
  const router = usePropagatedRouter();
  const { data: waitTime } = useGetClinicianWaitTime();
  const waitTimeMinutes = waitTime?.estimated_wait_minutes ?? 0;
  const waitTimeCopy =
    waitTimeMinutes > 0
      ? `~${waitTimeMinutes} min wait time`
      : DEFAULT_WAIT_TIME_COPY;

  const callObject = useDaily();
  const localSessionId = useLocalSessionId();
  const { show } = useToastController();
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<string | undefined>(
    undefined,
  );
  const [selectedAudio, setSelectedAudio] = useState<string | undefined>(
    undefined,
  );
  const [isAudioPopoverOpen, setIsAudioPopoverOpen] = useState(false);
  const [isVideoPopoverOpen, setIsVideoPopoverOpen] = useState(false);
  const [isPermissionsDialogOpen, setIsPermissionsDialogOpen] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const previousVisitStatusRef = useRef<string>(undefined);
  const {
    canJoinVideoCall,
    setCanJoinVideoCall,
    setIsVideoVisitStarted,
    setVideoMode,
    setVideoCallConversationId,
    videoCallConversationId,
  } = useVideoStore();

  const { data: userProfile } = useGetUserProfile();
  const userFirstName = userProfile?.firstName || "";
  const userLastName = userProfile?.lastName || "";
  const { data: clinicStatus } = useGetClinicStatusForPatient();
  const clinicianName =
    clinicStatus?.conversation?.curai_clinician_full_name_with_title ||
    `A ${medicalGroupName} clinician`;

  const { data: messagesData } = useInfiniteMessages(curaiConversationId);
  const allMessages = messagesData?.allMessages ?? [];

  const handleMissingDevices = useCallback(
    (videoInputs: MediaDeviceInfo[], audioInputs: MediaDeviceInfo[]) => {
      if (videoInputs.length > 0 && audioInputs.length > 0) return;

      const missingDevices = [];
      if (videoInputs.length === 0) missingDevices.push("camera");
      if (audioInputs.length === 0) missingDevices.push("microphone");
      show(
        `No ${missingDevices.join(" or ")} detected. Please check your device permissions and ensure a ${missingDevices.join(" and ")} ${missingDevices.length > 1 ? "are" : "is"} connected.`,
        { duration: 8000 },
      );

      captureException(new Error("No media devices detected"), {
        extra: {
          videoDevicesCount: videoInputs.length,
          audioDevicesCount: audioInputs.length,
        },
      });
    },
    [show],
  );

  const handleMediaError = useCallback(
    (error: Error) => {
      captureException(error, {
        extra: {
          context: "VideoPreview.initPreview",
          action: "startCamera or enumerateDevices",
          message: error.message,
        },
      });
      show(
        "Unable to access camera or microphone. Please check your browser permissions and try again.",
        { duration: 8000 },
      );
    },
    [show],
  );

  const clinicianPhotoUrl = useMemo(
    () =>
      allMessages.find((message) => message.type === "clinician_bio")
        ?.senderPhotoUrl,
    [allMessages],
  );

  const mostRecentVideoVisitEvent = useMemo(
    () =>
      allMessages
        .filter(
          (message) =>
            message.type === "video_visit_started" ||
            message.type === "video_visit_ended",
        )
        .reduce(
          (mostRecent, current) => {
            if (!mostRecent) {
              return current;
            }
            return current?.createdAt > mostRecent?.createdAt
              ? current
              : mostRecent;
          },
          undefined as ClientEventModel | undefined,
        ),
    [allMessages],
  );

  useEffect(() => {
    if (curaiConversationId && !videoCallConversationId) {
      setVideoCallConversationId(curaiConversationId);
    }
  }, [
    curaiConversationId,
    videoCallConversationId,
    setVideoCallConversationId,
  ]);

  useEffect(() => {
    if (
      mostRecentVideoVisitEvent?.type === "video_visit_started" &&
      !canJoinVideoCall
    ) {
      setCanJoinVideoCall(true);
    }
  }, [mostRecentVideoVisitEvent?.type, canJoinVideoCall, setCanJoinVideoCall]);

  useEffect(() => {
    const currentStatus = clinicStatus?.latest_visit?.status;
    const previousStatus = previousVisitStatusRef.current;
    previousVisitStatusRef.current = clinicStatus?.latest_visit?.status;
    if (
      curaiConversationId &&
      previousStatus === "completed" &&
      currentStatus !== "completed"
    ) {
      router.navigate(`/conversation/${curaiConversationId}`);
    }
    if (
      Boolean(previousStatus) &&
      previousStatus !== "completed" &&
      currentStatus === "completed"
    ) {
      onClose();
    }
  }, [
    curaiConversationId,
    clinicStatus?.latest_visit?.status,
    onClose,
    router,
  ]);

  useEffect(() => {
    return () => {
      setCanJoinVideoCall(false);
    };
  }, [setCanJoinVideoCall]);

  useVideoPreview(
    callObject,
    (devices) => {
      const videoInputs = devices.filter((d) => d.kind === "videoinput");
      const audioInputs = devices.filter((d) => d.kind === "audioinput");
      logMediaPermissions();
      setVideoDevices(videoInputs);
      setAudioDevices(audioInputs);
      handleMissingDevices(videoInputs, audioInputs);
      setSelectedVideo(videoInputs[0]?.deviceId);
      setSelectedAudio(audioInputs[0]?.deviceId);
    },
    handleMediaError,
  );

  const handleJoinCall = () => {
    if (canJoinVideoCall) {
      setVideoMode(VideoModes.FULLSCREEN);
      setIsVideoVisitStarted(true);
    }
  };

  const handleAudioDeviceChange = (deviceId: string) => {
    setSelectedAudio(deviceId);

    if (callObject) {
      callObject.setInputDevicesAsync({
        audioDeviceId: deviceId,
      });
    }
  };

  const handleVideoDeviceChange = (deviceId: string) => {
    setSelectedVideo(deviceId);

    if (callObject) {
      callObject.setInputDevicesAsync({
        videoDeviceId: deviceId,
      });
    }
  };

  const handleToggleMute = () => {
    if (callObject) {
      const newMutedState = !isMuted;
      setIsMuted(newMutedState);
      callObject.setLocalAudio(!newMutedState);
    }
  };

  const handleToggleCamera = () => {
    if (callObject) {
      const newCameraState = !isCameraOff;
      setIsCameraOff(newCameraState);
      callObject.setLocalVideo(!newCameraState);
    }
  };

  if (!callObject) {
    return (
      <YStack>
        <Text>Loading video preview...</Text>
      </YStack>
    );
  }

  return (
    <Theme name="clinic">
      <YStack flex={1} position="relative">
        <XStack
          paddingTop="$200"
          paddingHorizontal="$200"
          justifyContent="center"
        >
          <Logo.MedicalGroup
            height="$200"
            $md={{ height: "$250" }}
            $lg={{ height: "$300" }}
            color="$textPrimary"
          />

          <CloseButton
            position="absolute"
            right="$200"
            dataTrackingId="video-preview-close"
            onClose={onClose}
          />
        </XStack>

        <Header gap="$100" paddingTop="$200" paddingHorizontal="$200">
          <Heading size="$lg">
            {canJoinVideoCall ? (
              "Your clinician has joined"
            ) : (
              <>
                Connecting you to a clinician
                <Ellipses />
              </>
            )}
          </Heading>
          {!canJoinVideoCall && (
            <Paragraph size="$sm" lineHeight="$md" color="$textSecondary">
              {waitTimeCopy}. We will text you when your provider joins.
            </Paragraph>
          )}
          <Paragraph size="$sm" lineHeight="$md" color="$textSecondary">
            Before your visit, please make sure your camera and microphone are
            working. You may need to check your browser{" "}
            <LinkText
              size="$sm"
              bold
              onPress={() => setIsPermissionsDialogOpen(true)}
            >
              settings
            </LinkText>
            .
          </Paragraph>
        </Header>

        <ScrollView
          flex={1}
          contentContainerStyle={{
            gap: "$100",
            paddingBottom: "$200",
            paddingHorizontal: "$200",
          }}
        >
          <YStack
            gap="$150"
            flex={1}
            alignItems="center"
            justifyContent="center"
            marginTop="$300"
          >
            <YStack
              width={150}
              aspectRatio={3 / 4}
              $lg={{
                width: 280,
                aspectRatio: 7 / 5,
              }}
              borderRadius="$150"
              overflow="hidden"
              position="relative"
            >
              <LinearGradient
                colors={[
                  getTokenValue("$neutral700"),
                  getTokenValue("$neutral500"),
                ]}
                start={{ x: 0.5, y: 0 }}
                end={{ x: 0.5, y: 1 }}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                }}
              />
              <DailyVideo
                sessionId={localSessionId}
                type="video"
                automirror
                disablePictureInPicture
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "inherit",
                }}
              />
              <Text
                color="$textOnCall"
                fontWeight="600"
                fontSize="$md"
                position="absolute"
                top="$100"
                left="$100"
                zIndex={10}
              >
                {userFirstName} {userLastName}
              </Text>
            </YStack>

            <XStack gap="$100">
              <DeviceSelector
                devices={audioDevices}
                selectedDevice={selectedAudio}
                isOpen={isAudioPopoverOpen}
                onOpenChange={setIsAudioPopoverOpen}
                onDeviceChange={handleAudioDeviceChange}
                icon={Mic}
                iconOff={MicOff}
                isDisabled={isMuted}
                onToggle={handleToggleMute}
                ariaLabel="Select audio device"
                toggleAriaLabel={
                  isMuted ? "Unmute microphone" : "Mute microphone"
                }
              />
              <DeviceSelector
                devices={videoDevices}
                selectedDevice={selectedVideo}
                isOpen={isVideoPopoverOpen}
                onOpenChange={setIsVideoPopoverOpen}
                onDeviceChange={handleVideoDeviceChange}
                icon={Video}
                iconOff={VideoOff}
                isDisabled={isCameraOff}
                onToggle={handleToggleCamera}
                ariaLabel="Select video device"
                toggleAriaLabel={
                  isCameraOff ? "Turn camera on" : "Turn camera off"
                }
              />
            </XStack>

            <Paragraph size="$sm" color="$textSecondary">
              Having trouble?{" "}
              <Link
                size="$sm"
                bold
                href="https://www.curaihealth.com/support"
                external
              >
                Contact support
              </Link>
              .
            </Paragraph>
            <Paragraph
              italic
              size="$xs"
              lineHeight="$sm"
              textAlign="center"
              color="$textSecondary"
              $lg={{ flex: 1, textAlign: "left" }}
            >
              To ensure a positive experience for everyone, we ask that all
              interactions with our care team remain respectful, as threatening
              or harassing behavior may result in your visit being cancelled.
            </Paragraph>
          </YStack>
        </ScrollView>
        <Footer
          padding="$200"
          mode="dialog"
          $lg={{
            flexDirection: "row-reverse",
            alignItems: "center",
            gap: "$300",
          }}
        >
          <YStack flex={1} alignItems="flex-end" gap="$100">
            {canJoinVideoCall ? (
              <XStack gap="$100" alignSelf="center" $lg={{ alignSelf: "auto" }}>
                {clinicianPhotoUrl && (
                  <Image
                    source={{ uri: clinicianPhotoUrl }}
                    height="$300"
                    width="$300"
                    borderRadius={100_000}
                  />
                )}
                <Paragraph
                  size="$xs"
                  color="$textPrimary"
                  fontWeight="600"
                  alignSelf="center"
                >
                  {clinicianName} is in this call
                </Paragraph>
              </XStack>
            ) : (
              <Paragraph
                size="$xs"
                color="$textSecondary"
                alignSelf="center"
                textAlign="center"
                $lg={{ alignSelf: "auto", textAlign: "right" }}
              >
                No one is in this call yet. You'll be able to join shortly.
              </Paragraph>
            )}
            <Button
              mode="primary"
              width="100%"
              constrained
              icon={Video}
              onPress={handleJoinCall}
              disabled={!canJoinVideoCall}
              dataTrackingId="video-preview-join-button"
            >
              Join now
            </Button>
          </YStack>
        </Footer>
        <PermissionsDialog
          open={isPermissionsDialogOpen}
          onOpenChange={setIsPermissionsDialogOpen}
        />
      </YStack>
    </Theme>
  );
};

export default VideoPreview;
